import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getProject, listProjectVersions, forkProject } from '../../api/projects';
import { toast } from '../../stores/uiStore';
import { HorizonDashboard } from './HorizonDashboard';
import type { GrandProjet, VersionListItem } from '../../types/estimation';
import { fmt } from '../../utils/format';
import { useTranslation } from 'react-i18next';
import styles from './ReferenceProjectsPage.module.css';

export function ReferenceProjectDetailPage() {
  const { t } = useTranslation('common');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [project, setProject] = useState<GrandProjet | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showVersions, setShowVersions] = useState(false);
  const [versions, setVersions] = useState<VersionListItem[]>([]);
  const [forking, setForking] = useState(false);

  useEffect(() => {
    if (id) loadProject(id);
  }, [id]);

  async function loadProject(projectId: string) {
    try {
      setLoading(true);
      setError(null);
      const data = await getProject(projectId);
      if (data.is_custom) {
        setError('This is not a reference project');
        return;
      }
      setProject(data);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load project';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  async function handleUseAsTemplate() {
    if (!project) return;
    setForking(true);
    try {
      const forked = await forkProject(project.id);
      toast.success(`Project "${forked.name}" created from template.`);
      navigate(`/app/projects/${forked.id}`);
    } catch {
      toast.error('Failed to create project from template.');
    } finally {
      setForking(false);
    }
  }

  async function toggleVersions() {
    if (!project) return;
    if (showVersions) {
      setShowVersions(false);
      return;
    }
    try {
      const v = await listProjectVersions(project.id);
      setVersions(v);
      setShowVersions(true);
    } catch (err) {
      console.error('Failed to load versions:', err);
    }
  }

  function formatDate(iso: string) {
    const d = new Date(iso);
    return d.toLocaleString('fr-CH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  if (loading) return <div className={styles.loading}>{t('loading')}</div>;
  if (error)
    return (
      <div className={styles.page}>
        <div className={styles.loading} style={{ color: 'var(--color-danger, #e74c3c)' }}>{error}</div>
        <button className={styles.backBtn} onClick={() => navigate('/app/reference-projects')}>
          {t('backToProjects')}
        </button>
      </div>
    );
  if (!project) return null;

  const equipements = project.equipements as Record<string, number> | null;
  const hasEquipements = equipements && Object.keys(equipements).length > 0;

  // plq_refs is seeded as an array of objects (name, logements, sbp, ha, ius, source),
  // not strings — the earlier `as string[]` cast lied and React crashed with #31
  // "Objects are not valid as a React child". Accept both shapes.
  type PlqRef = string | { name?: string; source?: string };
  const plqRefs = (project.plq_refs ?? []) as PlqRef[];
  const plqRefLabel = (ref: PlqRef): string => {
    if (typeof ref === 'string') return ref;
    return ref?.name || ref?.source || JSON.stringify(ref);
  };

  return (
    <div className={styles.page}>
      <button className={styles.backBtn} onClick={() => navigate('/app/reference-projects')}>
        &larr; {t('backToProjects')}
      </button>

      {/* Header card */}
      <div className={styles.detailCard}>
        <div className={styles.detailHeader}>
          <div>
            <h2 className={styles.detailName}>{project.name}</h2>
            <span className={styles.detailType}>{project.type}</span>
          </div>
          <div className={styles.detailActions}>
            <button
              className={`${styles.actionBtn} ${styles.versionBtn}`}
              onClick={handleUseAsTemplate}
              disabled={forking}
              style={{ background: 'rgba(99,102,241,0.12)', borderColor: 'rgba(99,102,241,0.3)', color: '#6366f1' }}
            >
              {forking ? t('loading') : t('forkScenario')}
            </button>
            <button
              className={`${styles.actionBtn} ${styles.versionBtn}`}
              onClick={toggleVersions}
            >
              {showVersions ? t('hideVersions') : t('versions')}
            </button>
          </div>
        </div>

        {/* Metadata grid */}
        <div className={styles.metaGrid}>
          {project.ha_total != null && (
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>{t('surfaceTotale')}</span>
              <span className={styles.metaValue}>{project.ha_total} ha</span>
            </div>
          )}
          {project.ha_net != null && (
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>{t('surfaceNette')}</span>
              <span className={styles.metaValue}>{project.ha_net} ha</span>
            </div>
          )}
          {(project.ha_industriel ?? 0) > 0 && (
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>{t('industriel')}</span>
              <span className={styles.metaValue}>{project.ha_industriel} ha</span>
            </div>
          )}
          <div className={styles.metaItem}>
            <span className={styles.metaLabel}>{t('logements')}</span>
            <span className={styles.metaValue}>{fmt(project.logements)}</span>
          </div>
          <div className={styles.metaItem}>
            <span className={styles.metaLabel}>{t('emplois')}</span>
            <span className={styles.metaValue}>{fmt(project.emplois)}</span>
          </div>
          {project.horizon_years && (
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>{t('horizon')}</span>
              <span className={styles.metaValue}>{project.horizon_years} ans</span>
            </div>
          )}
          {project.sbp_logements > 0 && (
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>{t('sbpLogements')}</span>
              <span className={styles.metaValue}>{fmt(project.sbp_logements)} m&sup2;</span>
            </div>
          )}
          {project.sbp_activites > 0 && (
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>{t('sbpActivites')}</span>
              <span className={styles.metaValue}>{fmt(project.sbp_activites)} m&sup2;</span>
            </div>
          )}
          {project.sbp_total > 0 && (
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>{t('sbpTotal')}</span>
              <span className={styles.metaValue}>{fmt(project.sbp_total)} m&sup2;</span>
            </div>
          )}
          {project.espaces_publics_m2 != null && project.espaces_publics_m2 > 0 && (
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>{t('espacesPublics')}</span>
              <span className={styles.metaValue}>{fmt(project.espaces_publics_m2)} m&sup2;</span>
            </div>
          )}
          {project.ius_min != null && (
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>{t('ius')}</span>
              <span className={styles.metaValue}>
                {project.ius_min}{project.ius_max != null ? ` \u2013 ${project.ius_max}` : ''}
              </span>
            </div>
          )}
          {project.transit && (
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>{t('transit')}</span>
              <span className={styles.metaValue}>{project.transit}</span>
            </div>
          )}
        </div>

        {/* Communes */}
        {project.communes.length > 0 && (
          <div className={styles.communes}>
            <strong style={{ color: 'var(--color-text-muted)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              {t('communes')}:
            </strong>{' '}
            {project.communes.join(', ')}
          </div>
        )}

        {/* Notes */}
        {project.notes && <div className={styles.notes}>{project.notes}</div>}

        {/* Cost notes */}
        {project.cost_notes && (
          <div className={styles.costNotes}>
            <strong style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>{t('costNotes')}</strong>
            {project.cost_notes}
          </div>
        )}

        {/* Equipements */}
        {hasEquipements && (
          <div className={styles.equipSection}>
            <div className={styles.equipTitle}>{t('equipements')}</div>
            <div className={styles.equipGrid}>
              {Object.entries(equipements!).map(([key, value]) => (
                <div key={key} className={styles.equipItem}>
                  <span className={styles.equipKey}>{key.replace(/_/g, ' ')}</span>
                  <span className={styles.equipValue}>{value}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* PLQ references */}
        {plqRefs.length > 0 && (
          <div className={styles.plqSection}>
            <div className={styles.plqTitle}>{t('referencesPLQ')}</div>
            <div className={styles.plqList}>
              {plqRefs.map((ref, i) => (
                <span key={i} className={styles.plqBadge}>{plqRefLabel(ref)}</span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Version history */}
      {showVersions && (
        <div className={styles.versionsPanel}>
          <h3 className={styles.sectionTitle}>{t('versionHistory')}</h3>
          {versions.length === 0 ? (
            <div style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>{t('noVersions')}</div>
          ) : (
            <div className={styles.versionList}>
              {versions.map((v) => (
                <div key={v.id} className={styles.versionItem}>
                  <div className={styles.versionHeader}>
                    <span className={styles.versionNumber}>v{v.version_number}</span>
                    {v.is_current && <span className={styles.currentBadge}>{t('current')}</span>}
                    {v.rolled_back_from_version && (
                      <span style={{ color: 'var(--color-info)', fontSize: 11 }}>
                        {t('rollbackFrom', { version: v.rolled_back_from_version })}
                      </span>
                    )}
                  </div>
                  <div className={styles.versionSummary}>{v.change_summary || '\u2014'}</div>
                  <div className={styles.versionMeta}>
                    <span>{formatDate(v.created_at)}</span>
                    <span>
                      {v.created_by_user_id
                        ? `by ${v.created_by_user_id.slice(0, 8)}...`
                        : 'System'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Horizon analysis dashboard */}
      <HorizonDashboard project={project} />
    </div>
  );
}
